
import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import emailjs from '@emailjs/browser';
import { Mail } from 'lucide-react';
import ContactFormFields from './ContactFormFields';
import ContactFormSuccess from './ContactFormSuccess';
import { ContactFormData, ContactFormErrors } from './contactFormTypes';
import { validateContactForm, validateField } from './contactFormValidation';

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false
  });

  const [errors, setErrors] = useState<ContactFormErrors>({
    name: '',
    email: '',
    consent: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);

  useEffect(() => {
    if (networkError) {
      setNetworkError(null);
    }
  }, [formData, networkError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name as keyof typeof errors]) {
      const errorMessage = validateField(name as keyof ContactFormData, newValue as string | boolean);

      setErrors(prev => ({
        ...prev,
        [name]: errorMessage
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateContactForm(formData);

    if (validation.valid) {
      setIsSubmitting(true);
      setNetworkError(null);

      try {
        const templateParams = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          message: formData.message || 'No message provided',
        };

        console.log('Sending EmailJS with params:', templateParams);

        // Send each email individually with proper error handling
        try {
          const autoReplyResult = await emailjs.send(
            'service_3063xal',
            'template_hv5yj4b', // ✅ auto-reply to user
            templateParams,
            '2DrzQJZlfY82daC8E'
          );
          console.log('Auto-reply email sent:', autoReplyResult);
        } catch (autoReplyError) {
          console.error('Auto-reply email failed:', autoReplyError);
          throw new Error('Failed to send confirmation email');
        }

        try {
          const adminNotificationResult = await emailjs.send(
            'service_3063xal',
            'template_bse8vzu', // ✅ admin notification
            templateParams,
            '2DrzQJZlfY82daC8E'
          );
          console.log('Admin notification email sent:', adminNotificationResult);
        } catch (adminNotifyError) {
          console.error('Admin notification email failed:', adminNotifyError);
          throw new Error('Failed to send notification to administrator');
        }

        toast({
          title: "Message Sent!",
          description: "Thank you for contacting us. We'll get back to you soon!",
        });

        setIsSuccess(true);

        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: '',
            consent: false
          });
          setIsSuccess(false);
        }, 3000);
      } catch (error: any) {
        console.error('EmailJS error:', error);

        const errorMessage = error?.message || 'There was a problem sending your message. Please try again later.';
        setNetworkError(errorMessage);

        toast({
          title: "Message Failed to Send",
          description: errorMessage,
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className="premium-card">
      <h3 className="font-montserrat font-bold text-2xl md:text-3xl mb-6">Contact us</h3>
      <p className="text-lg mb-8 text-text/80 font-montserrat">Have questions or need more information? Reach out to us.</p>

      {isSuccess ? (
        <ContactFormSuccess />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {networkError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{networkError}</span>
            </div>
          )}

          <ContactFormFields 
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            isSubmitting={isSubmitting}
          />
        </form>
      )}
    </div>
  );
};

export default ContactForm;
